import React, { useState, useEffect } from 'react';
import {
  Shield,
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info,
  User,
  Eye,
  X,
  Building2
} from 'lucide-react';
import { notify } from '@core/notifications/notify';

interface AuditLog {
  id: string;
  actor_id: number;
  actor_name: string;
  actor_email: string;
  action: string;
  entity_type: string;
  entity_id: string;
  company_id: number;
  details: Record<string, any>;
  ip_address: string;
  created_at: string;
}

const PartnerAuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterEntity, setFilterEntity] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });

  useEffect(() => {
    loadLogs();
  }, [pagination.page]);

  const loadLogs = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
      });
      if (filterAction) params.append('action', filterAction);
      if (filterEntity) params.append('entity', filterEntity);
      if (filterStartDate) params.append('startDate', filterStartDate);
      if (filterEndDate) params.append('endDate', filterEndDate);

      const response = await fetch(`/api/partner/audit-logs?${params}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });

      if (response.ok) {
        const result = await response.json();
        setLogs(result.logs || []);
        if (result.total !== undefined) {
          setPagination(prev => ({ ...prev, total: result.total, pages: result.pages }));
        }
      } else {
        notify.error('Failed to load audit logs');
      }
    } catch {
      notify.error('Failed to load audit logs');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    loadLogs();
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilterAction('');
    setFilterEntity('');
    setFilterStartDate('');
    setFilterEndDate('');
    setPagination(prev => ({ ...prev, page: 1 }));
    setTimeout(loadLogs, 0);
  };

  const filteredLogs = logs.filter(log => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      log.action.toLowerCase().includes(q) ||
      log.actor_name.toLowerCase().includes(q) ||
      log.actor_email.toLowerCase().includes(q) ||
      log.entity_type.toLowerCase().includes(q)
    );
  });

  const getActionIcon = (action: string) => {
    const a = action.toLowerCase();
    if (a.includes('creat') || a.includes('add')) return <CheckCircle size={14} className="text-emerald-600" />;
    if (a.includes('delet') || a.includes('remov')) return <XCircle size={14} className="text-red-600" />;
    if (a.includes('updat') || a.includes('edit')) return <RefreshCw size={14} className="text-blue-600" />;
    if (a.includes('login') || a.includes('logout')) return <User size={14} className="text-purple-600" />;
    if (a.includes('error') || a.includes('fail')) return <AlertTriangle size={14} className="text-amber-600" />;
    return <Info size={14} className="text-slate-500" />;
  };

  const getActionBadge = (action: string) => {
    const a = action.toLowerCase();
    if (a.includes('creat') || a.includes('add')) return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
    if (a.includes('delet') || a.includes('remov')) return 'bg-red-100 text-red-700 border border-red-200';
    if (a.includes('updat') || a.includes('edit')) return 'bg-blue-100 text-blue-700 border border-blue-200';
    if (a.includes('login') || a.includes('logout')) return 'bg-purple-100 text-purple-700 border border-purple-200';
    if (a.includes('error') || a.includes('fail')) return 'bg-amber-100 text-amber-700 border border-amber-200';
    return 'bg-slate-100 text-slate-600 border border-slate-200';
  };

  const formatDate = (dt: string) => {
    try {
      return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      }).format(new Date(dt));
    } catch { return dt; }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-sky-600 shadow-md">
            <Shield size={22} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Portfolio Audit Logs</h1>
            <p className="text-sm text-slate-500">Activity across all your portfolio companies</p>
          </div>
        </div>
        <button
          onClick={() => { setPagination(prev => ({ ...prev, page: 1 })); loadLogs(); }}
          className="flex items-center gap-2 px-4 py-2 text-sm bg-white border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
        >
          <RefreshCw size={15} />
          Refresh
        </button>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-3xl border border-slate-100 p-4 flex items-center gap-3">
          <Shield size={20} className="text-sky-600 shrink-0" />
          <div>
            <p className="text-xs text-slate-500">Total Events</p>
            <p className="text-lg font-bold text-slate-900">{pagination.total.toLocaleString()}</p>
          </div>
        </div>
        <div className="bg-white rounded-3xl border border-slate-100 p-4 flex items-center gap-3">
          <CheckCircle size={20} className="text-emerald-600 shrink-0" />
          <div>
            <p className="text-xs text-slate-500">Created</p>
            <p className="text-lg font-bold text-slate-900">{logs.filter(l => l.action.toLowerCase().includes('creat')).length}</p>
          </div>
        </div>
        <div className="bg-white rounded-3xl border border-slate-100 p-4 flex items-center gap-3">
          <RefreshCw size={20} className="text-blue-600 shrink-0" />
          <div>
            <p className="text-xs text-slate-500">Updated</p>
            <p className="text-lg font-bold text-slate-900">{logs.filter(l => l.action.toLowerCase().includes('updat')).length}</p>
          </div>
        </div>
        <div className="bg-white rounded-3xl border border-slate-100 p-4 flex items-center gap-3">
          <XCircle size={20} className="text-red-500 shrink-0" />
          <div>
            <p className="text-xs text-slate-500">Deleted</p>
            <p className="text-lg font-bold text-slate-900">{logs.filter(l => l.action.toLowerCase().includes('delet')).length}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-3xl border border-slate-100 p-4 space-y-3">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-48">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search actions, users, entities..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-400"
            />
          </div>
          <input
            type="text"
            placeholder="Filter by action..."
            value={filterAction}
            onChange={e => setFilterAction(e.target.value)}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-400 w-44"
          />
          <input
            type="text"
            placeholder="Filter by entity type..."
            value={filterEntity}
            onChange={e => setFilterEntity(e.target.value)}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-400 w-44"
          />
          <input
            type="date"
            value={filterStartDate}
            onChange={e => setFilterStartDate(e.target.value)}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-400"
          />
          <input
            type="date"
            value={filterEndDate}
            onChange={e => setFilterEndDate(e.target.value)}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-400"
          />
          <button
            onClick={handleSearch}
            className="px-4 py-2 text-sm bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors"
          >
            Apply
          </button>
          {(filterAction || filterEntity || filterStartDate || filterEndDate || searchTerm) && (
            <button
              onClick={handleClearFilters}
              className="px-3 py-2 text-sm text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors flex items-center gap-1"
            >
              <X size={13} /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-48">
            <RefreshCw size={20} className="animate-spin text-sky-600" />
            <span className="ml-2 text-sm text-slate-500">Loading audit logs...</span>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-slate-400">
            <Shield size={32} className="mb-2 opacity-40" />
            <p className="text-sm">No audit logs found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Action</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Actor</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Entity</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Company</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Time</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredLogs.map(log => (
                    <tr key={log.id} className="hover:bg-slate-50/60 transition-colors">
                      <td className="px-4 py-3 text-left">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${getActionBadge(log.action)}`}>
                          {getActionIcon(log.action)}
                          {log.action.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-left">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-sky-100 flex items-center justify-center shrink-0">
                            <User size={12} className="text-sky-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800 text-xs">{log.actor_name}</p>
                            <p className="text-slate-400 text-xs">{log.actor_email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-left">
                        <div>
                          <p className="font-medium text-slate-700 text-xs">{log.entity_type}</p>
                          {log.entity_id && <p className="text-slate-400 text-xs">#{log.entity_id}</p>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-left">
                        <div className="flex items-center gap-1.5">
                          <Building2 size={12} className="text-slate-400 shrink-0" />
                          <span className="text-xs text-slate-600">Company {log.company_id}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-left text-xs text-slate-500 whitespace-nowrap">
                        {formatDate(log.created_at)}
                      </td>
                      <td className="px-4 py-3 text-left">
                        <button
                          onClick={() => setSelectedLog(log)}
                          className="p-1.5 text-slate-400 hover:text-sky-600 hover:bg-sky-50 rounded-lg transition-colors"
                          title="View details"
                        >
                          <Eye size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
                <p className="text-xs text-slate-500">
                  Showing {((pagination.page - 1) * pagination.limit) + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total.toLocaleString()} events
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                    className="p-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="text-xs text-slate-600">Page {pagination.page} of {pagination.pages}</span>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page === pagination.pages}
                    className="p-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setSelectedLog(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-sky-100">
                  <Shield size={18} className="text-sky-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Audit Log Detail</h3>
                  <p className="text-xs text-slate-500">{formatDate(selectedLog.created_at)}</p>
                </div>
              </div>
              <button onClick={() => setSelectedLog(null)} className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100 transition-colors">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1">Action</p>
                  <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${getActionBadge(selectedLog.action)}`}>
                    {getActionIcon(selectedLog.action)}
                    {selectedLog.action.replace(/_/g, ' ')}
                  </span>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1">Entity</p>
                  <p className="text-sm text-slate-800">{selectedLog.entity_type} <span className="text-slate-400">#{selectedLog.entity_id}</span></p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1">Actor</p>
                  <p className="text-sm font-medium text-slate-800">{selectedLog.actor_name}</p>
                  <p className="text-xs text-slate-400">{selectedLog.actor_email}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1">Company ID</p>
                  <p className="text-sm text-slate-800">{selectedLog.company_id}</p>
                </div>
                {selectedLog.ip_address && (
                  <div className="col-span-2">
                    <p className="text-xs font-medium text-slate-500 mb-1">IP Address</p>
                    <p className="text-sm text-slate-700 font-mono">{selectedLog.ip_address}</p>
                  </div>
                )}
              </div>
              {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-2">Details</p>
                  <pre className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-700 overflow-x-auto whitespace-pre-wrap">
                    {JSON.stringify(selectedLog.details, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerAuditLogs;
